import {skipToken} from '@reduxjs/toolkit/query';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';
import {noop} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {
  isTelephonyCallLive,
  TelephonyCallStateChangedPayloadSchema,
  useGetTelephonyCurrentCallQuery,
  useLazyGetTelephonyCallQuery,
} from '@dms/api/settings';

import {useInterval} from 'shared/web';

import {IDLE_CALL_DISCOVERY_INTERVAL_MS} from '../constants/IDLE_CALL_DISCOVERY_INTERVAL_MS';
import {LIVE_CALL_RESYNC_INTERVAL_MS} from '../constants/LIVE_CALL_RESYNC_INTERVAL_MS';
import {TELEPHONY_CALL_STATE_CHANGED_NOTIFICATION_TYPES} from '../constants/TELEPHONY_CALL_STATE_CHANGED_NOTIFICATION_TYPES';
import {isForbiddenApiError} from '../utils/isForbiddenApiError';
import {useApplyTelephonyCallState} from './useApplyTelephonyCallState';
import {useTelephonyCallAvailability} from './useTelephonyCallAvailability';

/**
 * Keeps the current-call cache entry in step with the backend. Every state transition arrives as a
 * `telephony-{outbound,inbound}_call_state_changed` background notification whose payload is the
 * whole call as the viewer sees it.
 * Pushes are best-effort and the channel has no replay, so the hook also re-reads the server:
 *
 * - `/telephony/calls/current`, on a slow interval while idle, every resync while a call is live,
 *   and on focus or reconnect, so a call started in another tab (or an inbound call ringing during
 *   a live outbound one) still shows up when its first pushes were missed;
 * - while a call is live, also the call itself (`GET /telephony/calls/{id}`), so a lost `ended`
 *   push is caught: `/current` answers with another call or `null`, which never ends this one.
 *
 * Every write goes through `mergeTelephonyCallState`, so no source can move the state backwards.
 * A 403 (licence off, role without TELEPHONY_CALL) stops the polling for the session.
 * Mount once per app (the top-bar indicator does), not per consumer.
 */
export function useTelephonyCallPushSync() {
  const applyState = useApplyTelephonyCallState();
  const {isAllowed} = useTelephonyCallAvailability();

  const {
    data: call,
    error,
    refetch,
  } = useGetTelephonyCurrentCallQuery(isAllowed ? undefined : skipToken, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [getTelephonyCall] = useLazyGetTelephonyCallQuery();

  const liveCall = isNotNil(call) && isTelephonyCallLive(call) ? call : null;

  useSubscribeToNotificationsQuery((notification) => {
    const payload = match(notification)
      .with(
        {type: Pattern.union(...TELEPHONY_CALL_STATE_CHANGED_NOTIFICATION_TYPES)},
        (callNotification) => callNotification.payload
      )
      .otherwise(() => null);

    const parsed = TelephonyCallStateChangedPayloadSchema.safeParse(payload);

    if (!parsed.success) {
      return;
    }

    const {callId, ...state} = parsed.data;

    applyState({id: callId, ...state});
  });

  const resync = () => {
    refetch();

    if (isNotNil(liveCall)) {
      getTelephonyCall({callId: liveCall.id}, false).unwrap().then(applyState).catch(noop);
    }
  };

  const isPollingAllowed = isAllowed && !isForbiddenApiError(error);

  const resyncIntervalMs = match([isPollingAllowed, isNotNil(liveCall)])
    .with([false, Pattern.boolean], () => null)
    .with([true, true], () => LIVE_CALL_RESYNC_INTERVAL_MS)
    .with([true, false], () => IDLE_CALL_DISCOVERY_INTERVAL_MS)
    .exhaustive();

  useInterval(resync, resyncIntervalMs);
}
